import { ICellRendererAngularComp } from "@ag-grid-community/angular";
import { ICellRendererParams } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, computed, signal } from "@angular/core";
import { StrategyAsin } from "@front/m19-api-client";
import { IBadgeComponent, ITooltipDirective } from "@front/m19-ui";
import {
  BulkUploadFieldPreview,
  BulkUploadRowPreview,
  BulkUploadRunResultItem,
  SupportedBulkUploadEntityModel,
} from "@m19-board/services/bulk-upload/bulk-upload-context.service";

interface AsinDiff {
  added: string[];
  removed: string[];
  total: number;
}

type AsinCellRendererValue<T> = BulkUploadFieldPreview<T> | StrategyAsin[] | string[] | null | undefined;
type AsinCellRendererRow<T> = BulkUploadRowPreview<T> | BulkUploadRunResultItem<T>;

@Component({
  selector: "app-bulk-upload-asin-cell-renderer",
  templateUrl: "./bulk-upload-asin-cell-renderer.component.html",
  imports: [IBadgeComponent, ITooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkUploadAsinCellRendererComponent<T extends SupportedBulkUploadEntityModel>
  implements ICellRendererAngularComp
{
  protected readonly fieldPreview = signal<BulkUploadFieldPreview<T> | null | undefined>(undefined);
  protected readonly resultAsinCount = signal<number | undefined>(undefined);

  protected readonly isResultMode = computed(() => this.resultAsinCount() !== undefined);

  protected readonly diff = computed<AsinDiff>(() => {
    const preview = this.fieldPreview();
    const next = toAsinList(preview?.parsedValue);
    const prev = toAsinList(preview?.existingValue);
    const prevSet = new Set(prev);
    const nextSet = new Set(next);
    return {
      added: next.filter((a) => !prevSet.has(a)),
      removed: prev.filter((a) => !nextSet.has(a)),
      total: nextSet.size,
    };
  });

  protected readonly showDiff = computed(() => {
    const preview = this.fieldPreview();
    return !!preview?.hasChanged && !preview.isCreation;
  });

  protected readonly resultCount = computed(() => this.resultAsinCount() ?? 0);

  agInit(params: ICellRendererParams<AsinCellRendererRow<T>, AsinCellRendererValue<T>>): void {
    const value = params.value;
    if (Array.isArray(value)) {
      this.resultAsinCount.set(toAsinList(value).length);
    } else {
      this.fieldPreview.set(value);
    }
  }

  refresh(_: ICellRendererParams<AsinCellRendererRow<T>>): boolean {
    return true;
  }
}

function toAsinList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((a) => (typeof a === "string" ? a : (a as StrategyAsin)?.asin)).filter((a): a is string => !!a);
}
